import React, { useMemo } from "react";
import { IonIcon } from "@ionic/react";
import { downloadOutline } from "ionicons/icons";
import { useConfig } from "../../providers/config";
import { Capacitor } from "@capacitor/core";

const FALLBACK_IOS_URL = "https://apps.apple.com/ru/app/parhato/id1572201263";
const FALLBACK_ANDROID_URL = "https://play.google.com/store/apps/details?id=ru.parhato.app";

const resolveInstallUrl = (iosUrl: string, androidUrl: string) => {
  const platform = Capacitor.getPlatform();

  if (platform === "ios") return iosUrl || androidUrl;
  if (platform === "android") return androidUrl || iosUrl;

  const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isIOS = /iPad|iPhone|iPod/i.test(userAgent);
  const isAndroid = /Android/i.test(userAgent);

  if (isIOS) return iosUrl || androidUrl;
  if (isAndroid) return androidUrl || iosUrl;

  return androidUrl || iosUrl;
};

interface MobileAppInstallCtaPanelProps {
  compact?: boolean;
}

const MobileAppInstallCtaPanel = ({ compact = false }: MobileAppInstallCtaPanelProps) => {
  const config = useConfig();
  const isNativePlatform = Capacitor.getPlatform() !== "web";

  const installUrl = useMemo(() => {
    const iosUrl = config?.sharedData?.iosUrl || FALLBACK_IOS_URL;
    const androidUrl = config?.sharedData?.androidUrl || FALLBACK_ANDROID_URL;

    return resolveInstallUrl(iosUrl, androidUrl);
  }, [config?.sharedData?.iosUrl, config?.sharedData?.androidUrl]);

  // Never show install CTA inside native mobile app builds.
  if (isNativePlatform || !installUrl) return null;

  if (compact) {
    return (
      <a
        href={installUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="md:hidden inline-flex items-center gap-1.5 bg-[#F2F7FF] border border-[#D6E7FF] rounded-xl px-2 py-1.5"
      >
        <IonIcon icon={downloadOutline} className="text-sm text-[#1F3556]" />
        <span className="text-[11px] font-bold text-[#1F3556] leading-none">Установить</span>
      </a>
    );
  }

  return (
    <div className="mx-2.5 mb-3 md:hidden">
      <a
        href={installUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-between gap-3 p-3 bg-[#F2F7FF] border border-[#D6E7FF] rounded-2xl shadow-sm"
      >
        <div className="flex flex-col">
          <span className="font-bold text-[#1F3556] text-sm">Установите приложение</span>
          <span className="text-[#5D7695] text-xs">Быстрее и удобнее в мобильном приложении</span>
        </div>
        <span className="bg-primary hover:bg-primary-shade active:bg-primary-tint transition-colors px-4 py-2 rounded-[20px] flex items-center gap-2 font-bold text-sm text-[#1F1F1F]">
          <span>Установить</span>
          <IonIcon icon={downloadOutline} className="text-base" />
        </span>
      </a>
    </div>
  );
};

export default MobileAppInstallCtaPanel;
